/// <reference types="../@types/post-robot" />
import postRobot from "post-robot";

import { EventEmitter } from "./helpers";
import {
  CashReceivingRequest,
  CashReceivingResult,
  CashStatusChangeListener,
  CloseRequestListener,
  EmailReceiptRequest,
  ICancelableType,
  KioskSdkOptions,
  Payment,
  PrintReceiptRequest,
  PrintReceiptResult,
  ReceiptAttributes,
  ResultWrapper,
  SessionData,
  TransactionChangedListener,
} from "./interfaces";

interface SdkMethods {
  [key: string]: (...args: any[]) => any;
}

export class KioskSdk {
  eventEmitter: EventEmitter;
  options: KioskSdkOptions;

  private defaultOptions = {
    timeout: 5000,
    logging: false,
    target: window.parent,
  } as KioskSdkOptions;

  private subscriptions: ICancelableType[] = [];
  private userActivityListener: any;

  constructor(options: KioskSdkOptions) {
    this.eventEmitter = new EventEmitter();
    this.userActivityListener = this.userActivity.bind(this);

    this.options = Object.assign({}, this.defaultOptions, options);
  }

  register(): void {
    window.document.addEventListener("click", this.userActivityListener);

    const sdkOptions = {};
    const sdkMethods: SdkMethods = {
      //for knowning that app opened successfully
      handshake: function () {
        return {
          handshake: true,
        };
      },

      closeRequest: (data: any) => {
        // Emit event, to notify all listeners and wait handlers completition
        return this.eventEmitter.emit("onclose", data);
      },

      cashAcceptorStatusChanged: (data: any) => {
        return this.eventEmitter.emit("oncashacceptorstatuschange", data);
      },

      transactionChanged: (data: any) => {
        return this.eventEmitter.emit("ontransactionchange", data);
      },

      userActivity: (data: any) => {
        return this.eventEmitter.emit("onuseractivity", data);
      },
    };

    this.subscriptions = Object.keys(sdkMethods).map((k) =>
      postRobot.on(k, sdkOptions, (...args: any[]) => {
        if (this.options.logging) {
          console.log(`[Kiosk Sdk] Received event '${k}'`, ...args);
        }

        return sdkMethods[k](...args);
      })
    );

    if (this.options.logging) {
      console.log(`Registered`, this.subscriptions);
    }
  }

  unregister(): void {
    this.subscriptions.forEach((s) => s.cancel());
    this.subscriptions = [];

    window.document.removeEventListener("click", this.userActivityListener);

    if (this.options.logging) {
      console.log(`Unregistered`);
    }
  }

  /**
   * Get Current Kiosk Session Data
   * @param {KioskSdkOptions} [options]
   * @returns {Promise<ResultWrapper<SessionData>>}
   * @memberof KioskSdk
   */
  getSession(options?: KioskSdkOptions): Promise<ResultWrapper<SessionData>> {
    return this.send("GetSession", {}, options);
  }

  goBack(options?: KioskSdkOptions): Promise<ResultWrapper<any>> {
    return this.send("GoBack", {}, options);
  }
  goHome(options?: KioskSdkOptions): Promise<ResultWrapper<any>> {
    return this.send("GoHome", {}, options);
  }
  goPayment(
    data: Payment,
    options?: KioskSdkOptions
  ): Promise<ResultWrapper<any>> {
    return this.send("GoPayment", data, options);
  }
  ping(options?: KioskSdkOptions): Promise<ResultWrapper<any>> {
    return this.send("Ping", {}, options);
  }
  startProcessingCash(
    request: CashReceivingRequest,
    options?: KioskSdkOptions
  ): Promise<ResultWrapper<CashReceivingResult>> {
    return this.send("StartProcessingCash", request, options);
  }
  stackCash(options?: KioskSdkOptions): Promise<ResultWrapper<any>> {
    return this.send("StackCash", {}, options);
  }
  returnCash(options?: KioskSdkOptions): Promise<ResultWrapper<any>> {
    return this.send("ReturnCash", {}, options);
  }
  stopProcessingCash(options?: KioskSdkOptions): Promise<ResultWrapper<any>> {
    return this.send("StopProcessingCash", {}, options);
  }
  printReceipt(
    data: PrintReceiptRequest,
    options?: KioskSdkOptions
  ): Promise<ResultWrapper<PrintReceiptResult>> {
    return this.send("PrintReceipt", data, options);
  }
  emailReceipt(
    data: EmailReceiptRequest,
    options?: KioskSdkOptions
  ): Promise<ResultWrapper<any>> {
    return this.send("EmailReceipt", data, options);
  }
  homeButtonHide(options?: KioskSdkOptions): Promise<ResultWrapper<any>> {
    return this.send("HomeButtonHide", {}, options);
  }
  homeButtonShow(options?: KioskSdkOptions): Promise<ResultWrapper<any>> {
    return this.send("HomeButtonShow", {}, options);
  }
  getReceiptAttributes(
    options?: KioskSdkOptions
  ): Promise<ResultWrapper<ReceiptAttributes>> {
    return this.send("ReceiptAttributes", {}, options);
  }
  userActivity(
    data: any,
    options?: KioskSdkOptions
  ): Promise<ResultWrapper<any>> {
    return this.send("UserActivity", data, options);
  }

  addTransactionChangedListener(listener: TransactionChangedListener): number {
    return this.eventEmitter.addListener("ontransactionchange", listener);
  }

  logError(
    message: string,
    options?: KioskSdkOptions
  ): Promise<ResultWrapper<any>> {
    return this.send("LogError", message, options);
  }

  /**
   * @param {CloseRequestListener} listener is a function which should return promise, if promise is rejected, Kommon will not close application
   * @returns {number}
   * @memberof KioskSdk
   */
  addCloseRequestListener(listener: CloseRequestListener): number {
    return this.eventEmitter.addListener("onclose", listener);
  }
  addCashAcceptorStatusChangeListener(
    listener: CashStatusChangeListener
  ): number {
    return this.eventEmitter.addListener(
      "oncashacceptorstatuschange",
      listener
    );
  }
  addUserActivityListener(listener: Function): number {
    return this.eventEmitter.addListener("onuseractivity", listener);
  }
  removeTransactionChangedListener(listenerId: number): void {
    return this.eventEmitter.removeListenerById(
      "ontransactionchange",
      listenerId
    );
  }
  removeCloseRequestListener(listenerId: number): void {
    return this.eventEmitter.removeListenerById("onclose", listenerId);
  }
  removeCashAcceptorStatusChangeListener(listenerId: number): void {
    return this.eventEmitter.removeListenerById(
      "oncashacceptorstatuschange",
      listenerId
    );
  }
  removeUserActivityListener(listenerId: number): void {
    return this.eventEmitter.removeListenerById("onuseractivity", listenerId);
  }

  private send(
    method: string,
    data: any,
    options?: KioskSdkOptions
  ): Promise<any> {
    options = Object.assign({}, this.options, options);

    if (options!.logging) {
      console.log(`[Kiosk Sdk] Sending '${method}' is: `, data, options);
    }

    return postRobot.send(this.options.target, method, data, options).then(
      (event) => {
        if (options!.logging) {
          console.log(`[Kiosk Sdk] Response for '${method}' is: `, event.data);
        }
        return event;
      },
      (error) => {
        if (options!.logging) {
          console.error(
            `[Kiosk Sdk] Error response for '${method}' is: `,
            error
          );
        }
        return Promise.reject(error);
      }
    );
  }
}
